import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnChanges,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
  input,
  output,
  signal,
} from '@angular/core';
import * as pdfjsLib from 'pdfjs-dist';
import { HxButton } from '../button/button';
import './pdfjs-worker-setup';

/**
 * Renders a PDF blob as fit-to-width paginated canvases, with an optional filename + Download/Close actions bar. Used
 * both inside a modal dialog (actions shown) and embedded directly in a page layout (actions hidden).
 */
@Component({
  selector: 'hx-pdf-viewer',
  standalone: true,
  imports: [HxButton],
  templateUrl: './pdf-viewer.html',
  styleUrl: './pdf-viewer.css',
  host: { class: 'hx-pdf-viewer' },
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HxPdfViewer implements OnChanges {
  /** The PDF file to render. */
  readonly blob = input<Blob | null>(null);
  /** Displayed next to the actions bar when shown. */
  readonly fileName = input<string>('');
  /** Disables the Download button while a download is in progress. */
  readonly isDownloading = input<boolean>(false);
  /** Shows the Download button in the actions bar. */
  readonly showDownloadAction = input<boolean>(true);
  /** Shows the Close button in the actions bar. */
  readonly showCloseAction = input<boolean>(true);

  readonly download = output<void>();
  readonly close = output<void>();

  @ViewChild('pageContainer')
  private pageContainer?: ElementRef<HTMLDivElement>;

  readonly isLoading = signal(true);
  readonly errorMessage = signal<string | null>(null);
  private pdfDocument?: pdfjsLib.PDFDocumentProxy;

  get showActions(): boolean {
    return this.showDownloadAction() || this.showCloseAction();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['blob'] && this.blob()) {
      void this.loadPdf();
    }
  }

  private async loadPdf(): Promise<void> {
    this.isLoading.set(true);
    this.errorMessage.set(null);
    try {
      const data = await this.blob()!.arrayBuffer();
      this.pdfDocument = await pdfjsLib.getDocument({ data }).promise;
      await this.renderPages();
    } catch {
      this.errorMessage.set('This PDF could not be displayed. Please try again.');
    } finally {
      this.isLoading.set(false);
    }
  }

  private async renderPages(): Promise<void> {
    const container = this.pageContainer?.nativeElement;
    if (!container || !this.pdfDocument) {
      return;
    }

    // Fit-to-width: the page fills the container's width at 100% and the
    // container scrolls vertically for the rest of the page / subsequent
    // pages. No zoom control - this is the only scale used.
    const availableWidth = Math.max(container.clientWidth - 24, 280);

    // Render every page into a detached DocumentFragment first, then swap
    // it into the live DOM in a single operation. Appending pages one at a
    // time while each is still rendering causes the container to visibly
    // grow from empty to full size (layout "shaking").
    const fragment = document.createDocumentFragment();

    for (let pageNumber = 1; pageNumber <= this.pdfDocument.numPages; pageNumber += 1) {
      const page = await this.pdfDocument.getPage(pageNumber);
      const baseViewport = page.getViewport({ scale: 1 });
      const fitScale = availableWidth / baseViewport.width;
      const viewport = page.getViewport({ scale: fitScale });
      const pageSurface = document.createElement('div');
      pageSurface.className = 'hx-pdf-viewer__page';
      pageSurface.style.width = `${viewport.width}px`;
      pageSurface.style.height = `${viewport.height}px`;

      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      if (!context) {
        continue;
      }

      const outputScale = Math.max(window.devicePixelRatio || 1, 2);
      canvas.width = Math.floor(viewport.width * outputScale);
      canvas.height = Math.floor(viewport.height * outputScale);
      canvas.style.width = `${viewport.width}px`;
      canvas.style.height = `${viewport.height}px`;
      context.setTransform(outputScale, 0, 0, outputScale, 0, 0);
      pageSurface.appendChild(canvas);
      fragment.appendChild(pageSurface);
      await page.render({
        canvas,
        canvasContext: context,
        viewport,
      }).promise;
    }

    container.replaceChildren(fragment);
  }
}
